/**
 * File types the extension can use on `context.file`.
 * Documents (PDF/Office) → uploaded for full-content read;
 * text → inlined; images → vision.
 */

export const AI_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const AI_MAX_DATA_JSON_CHARS = 100_000;

export const AI_UNSUPPORTED_FILE_TOOLTIP =
    'This file type is not supported for AI. Supported: PDF, Word, Excel, RTF, text, CSV, JSON, and images.';

export const AI_FILE_TOO_LARGE_TOOLTIP = 'File is too large for AI (max 10 MB).';

export const AI_FILE_MISSING_TOOLTIP = 'This item has no downloadable file to attach.';

/** Attach picker group keys that send bytes via `context.file` (one per turn). */
export const AI_FILE_ATTACH_GROUP_KEYS = ['documents', 'letters', 'faxes'] as const;
export type AiFileAttachGroupKey = (typeof AI_FILE_ATTACH_GROUP_KEYS)[number];

export const isAiFileAttachGroupKey = (key: string): key is AiFileAttachGroupKey =>
    (AI_FILE_ATTACH_GROUP_KEYS as readonly string[]).includes(key);

const SUPPORTED_DOCUMENT_TYPES = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/rtf',
    'text/rtf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const SUPPORTED_TEXT_TYPES = new Set([
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/json',
    'application/csv',
]);

const SUPPORTED_IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
]);

const guessMimeFromFilename = (filename?: string): string | undefined => {
    if (!filename) {
        return undefined;
    }
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.doc')) return 'application/msword';
    if (lower.endsWith('.docx')) {
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    if (lower.endsWith('.rtf')) return 'application/rtf';
    if (lower.endsWith('.xls')) return 'application/vnd.ms-excel';
    if (lower.endsWith('.xlsx')) {
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (lower.endsWith('.json')) return 'application/json';
    if (lower.endsWith('.csv')) return 'text/csv';
    if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'text/markdown';
    if (lower.endsWith('.txt') || lower.endsWith('.log')) return 'text/plain';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.webp')) return 'image/webp';
    return undefined;
};

/** True when the extension accepts this MIME / extension for `context.file`. */
export const isAiFileContentSupported = (mimetype?: string, filename?: string): boolean => {
    const type = (mimetype || guessMimeFromFilename(filename) || '').toLowerCase().trim();
    if (!type) {
        return false;
    }
    if (SUPPORTED_DOCUMENT_TYPES.has(type) || type.endsWith('+pdf')) {
        return true;
    }
    if (SUPPORTED_TEXT_TYPES.has(type) || type.startsWith('text/')) {
        // Extension listed specific text types; allow other text/* as safe inline.
        return SUPPORTED_TEXT_TYPES.has(type) || type.startsWith('text/');
    }
    if (SUPPORTED_IMAGE_TYPES.has(type)) {
        return true;
    }
    return false;
};

export type AiDocumentAttachState = {
    disabled: boolean;
    disabledReason?: string;
};

export const getDocumentAiAttachState = (doc: {
    mimetype?: string;
    filename?: string;
    name?: string;
    filesize?: number;
    size?: number;
    /** When false/undefined and no mime/filename, treat as missing file. */
    hasFile?: boolean;
}): AiDocumentAttachState => {
    if (doc.hasFile === false) {
        return { disabled: true, disabledReason: AI_FILE_MISSING_TOOLTIP };
    }

    const filename = doc.filename || doc.name;
    const size = typeof doc.filesize === 'number' ? doc.filesize : doc.size;
    const mime = doc.mimetype || guessMimeFromFilename(filename);

    if (!mime && !filename) {
        // Linked file with unknown metadata — allow attach; download will resolve MIME.
        // (Missing file already returned above when hasFile === false.)
        return { disabled: false };
    }

    if (!isAiFileContentSupported(mime, filename)) {
        return { disabled: true, disabledReason: AI_UNSUPPORTED_FILE_TOOLTIP };
    }
    if (typeof size === 'number' && size > AI_MAX_FILE_BYTES) {
        return { disabled: true, disabledReason: AI_FILE_TOO_LARGE_TOOLTIP };
    }
    return { disabled: false };
};
